
































































































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { validationMixin } from "vuelidate";
import { EditorFileStore } from "./FileStoreEditModal.vue";
import { S3FileStoreConfig } from "@/types/settings/filestores/S3FileStoreConfig";
import { required, numeric } from "vuelidate/lib/validators";
import { debounce } from "lodash";
import { stringMixin } from "@/mixins";
import vSelect from "vselect3";

type EndpointTypes = "AWS Region" | "Custom Endpoint";

const { isNullOrWhitespace } = stringMixin.methods;

const validations = function() {
	// Base validators which apply regardless of what kind of S3 connection the user wants to use.
	const baseValidators = {
		AccessId: {
			required
		},
		AccessKey: {
			required
		},
		Bucket: {
			required
		}
	};

	let validationSchema;

	if (this.endpointType == "AWS Region") {
		// Only the Region needs to be specified.
		validationSchema = {
			...baseValidators,
			Region: {
				required
			}
		};
	} else if (this.endpointType == "Custom Endpoint") {
		// The user must specify a URL, and an optional (numeric) port.
		validationSchema = {
			...baseValidators,
			EndpointUrl: {
				required
			},
			EndpointPort: {
				numeric
			}
		};
	}

	return {
		s3Config: validationSchema
	};
};

@Component({
	mixins: [validationMixin],
	validations,
	model: {
		prop: "fileStore",
		event: "input"
	},
	components: {
		"v-select": vSelect
	}
})
export default class S3Configuration extends Vue {
	@Prop({ type: Object, default: null })
	private fileStore: EditorFileStore;

	private readonly endpointTypes = ["AWS Region", "Custom Endpoint"];
	private readonly awsRegions = [
		{ value: "us-east-2", label: "US East (Ohio)" },
		{ value: "us-east-1", label: "US East (N. Virginia)" },
		{ value: "us-west-1", label: "US West (N. California)" },
		{ value: "us-west-2", label: "US West (Oregon)" },
		{ value: "eu-west-1", label: "Europe (Ireland)" },
		{ value: "eu-west-2", label: "Europe (London)" },
		{ value: "eu-west-3", label: "Europe (Paris)" },
		{ value: "eu-south-1", label: "Europe (Milan) " },
		{ value: "eu-north-1", label: "Europe (Stockholm)" },
		{ value: "eu-central-1", label: "Europe (Frankfurt)" }
	];

	private endpointType: EndpointTypes = "AWS Region";
	private s3Config: S3FileStoreConfig = {
		AccessId: "",
		AccessKey: "",
		Bucket: "",
		Region: null,
		EndpointUrl: "",
		EndpointPort: null,
		DisableChunkEncoding: false
	};

	private showSecretKey: boolean = false;

	/**
	 * Watcher which fires when the filestore changes in the parent. Ensures the S3 config we're editing is kept
	 * up-to-date with the config in the fileStore prop.
	 */
	@Watch("fileStore", { immediate: true })
	private onFileStoreChanged(newFileStore: EditorFileStore) {
		try {
			// Parse the S3 config
			const deserializedConfig: S3FileStoreConfig = JSON.parse(newFileStore.config);

			if (!deserializedConfig) {
				// No config, so just return instead.
				return;
			}

			// Set the correct endpoint type
			if (!isNullOrWhitespace(deserializedConfig.Region)) {
				this.endpointType = "AWS Region";
			} else {
				this.endpointType = "Custom Endpoint";
			}

			this.s3Config = deserializedConfig;
		} catch (err) {
			console.error(`Unable to parse S3 configuration for Filestore: ${newFileStore.config}: ${err}`);
		}
	}

	/**
	 * Watcher which fires when our validation state changes. Emits a validate event, along with a value
	 * indicating whether the filestore is valid or not.
	 */
	@Watch("$v.$invalid", { immediate: true })
	private onValidationStateChanged(invalid: boolean) {
		this.$emit("validate", !invalid);
	}

	/**
	 * Handler for when the S3 configuration is changed by the user. Debounced to ensure we don't emit our input
	 * event too often.
	 */
	@Watch("s3Config", { deep: true })
	private onConfigurationChanged = debounce(function() {
		if (this.$v.$invalid) {
			return;
		}

		// Serialize the S3 config and add it to the fileStore
		const config = JSON.stringify(this.s3Config);
		const updatedFileStore: EditorFileStore = {
			...this.fileStore,
			config
		};

		this.$emit("input", updatedFileStore);
	});
}
